import { Injectable } from '@angular/core';
import type {
  BridgeReply,
  NativeAudioState,
  TelemedNativeBridge,
} from '../../../types/native-bridge';
import {
  bridgeFail,
  bridgeOk,
  type BridgeResult,
  type NativeBridge,
  type StethoscopeStats,
  type UsbDeviceState,
} from './native-bridge';

/**
 * Extra time allowed beyond the host's own timeout.
 *
 * The host posts its result across a thread boundary, so the callback lands
 * fractionally after the deadline it was given. Without this margin the web
 * side would frequently declare a timeout for a route that had in fact settled.
 */
const ROUTE_SETTLE_GRACE_MS = 250;

/** The message the host sends when handing over the PCM channel. */
const PCM_PORT_HANDSHAKE = 'pcm-port';

/**
 * Call a TelemedBridge method and interpret its reply.
 *
 * Every method returns a JSON string rather than a boolean, so that a failure
 * can say *why*. A reply that will not parse is treated as a failure rather
 * than as success — §10's rule that a failure must never be indistinguishable
 * from an empty success.
 */
function callBridge(
  method: keyof TelemedNativeBridge,
  invoke: (bridge: TelemedNativeBridge) => string,
): BridgeResult {
  const bridge = window.TelemedBridge;
  if (!bridge) {
    return bridgeFail(`host does not implement ${String(method)}`);
  }

  try {
    const reply = JSON.parse(invoke(bridge)) as BridgeReply;
    return reply.ok
      ? bridgeOk(undefined)
      : bridgeFail(reply.error ?? `${String(method)} failed`);
  } catch (error) {
    return bridgeFail(`${String(method)} returned an unreadable reply: ${String(error)}`);
  }
}

/**
 * The real Android host.
 *
 * Written against the APK as it exists today, with the methods the architecture
 * requires treated as optional and feature-detected. That is deliberate: the
 * native side is a separate codebase on its own schedule, and an app that only
 * runs against a host that does not exist yet cannot be tested against the one
 * that does.
 *
 * Where a capability is missing, this reports a clear reason rather than
 * pretending the operation succeeded. A silent no-op here would present as the
 * §3 sequence having run when it did not.
 */
@Injectable()
export class AndroidNativeBridge implements NativeBridge {
  private pcmPortHandler: ((port: MessagePort) => void) | null = null;
  private deviceStateHandler: ((state: UsbDeviceState) => void) | null = null;
  private errorHandler: ((message: string) => void) | null = null;
  private routeRestoredHandler: ((message: string) => void) | null = null;

  private readonly registeredCallbacks: Array<keyof Window> = [];
  private readonly registeredListeners: Array<(event: MessageEvent) => void> = [];
  private pcmPortListener: ((event: MessageEvent) => void) | null = null;

  /**
   * Highest generation stamp seen from the host.
   *
   * The APK stamps its mic pause/resume calls with an incrementing generation
   * and expects the page to ignore anything older. Without this, a release that
   * was waiting on the Jitsi API can mute the call *after* its matching restore
   * has already run — the call then stays muted with nothing obviously wrong.
   */
  private highestGeneration = -1;

  constructor() {
    this.registerHostCallbacks();
  }

  isNative(): boolean {
    return typeof window.TelemedBridge !== 'undefined';
  }

  async startSession(sessionId: string): Promise<BridgeResult> {
    if (!this.isNative()) {
      return bridgeFail('no Android bridge present');
    }

    if (window.TelemedBridge) {
      // Clears any inherited native capture state before this session begins
      // (§10A.4). The correlation id also reaches the native logs, so one
      // consultation is one timeline across both halves.
      return callBridge('startSession', (b) => b.startSession(sessionId));
    }

    // An older host has no session concept; capture begins from its own toggle.
    return bridgeOk(undefined);
  }

  /**
   * Ordered teardown that guarantees the process-global capture flag is
   * cleared, whatever else failed on the way (§10A).
   */
  async endSession(reason: string): Promise<BridgeResult> {
    return callBridge('endSession', (b) => b.endSession(reason));
  }

  async selectBuiltInCommDevice(): Promise<BridgeResult> {
    // §3.5 step 2. On a host that predates this contract the call fails, and
    // the web layer's post-creation verification becomes the only guard — it
    // then detects a bad binding rather than preventing one.
    return callBridge('selectBuiltInCommDevice', (b) =>
      b.selectBuiltInCommDevice(),
    );
  }

  /**
   * §3.5 step 3.
   *
   * The host cannot answer synchronously — it observes an AudioManager callback
   * delivered on the main looper, while `@JavascriptInterface` methods run on a
   * background thread — so it reports through `window.onRouteSettled` and this
   * waits for whichever comes first, the callback or the timeout.
   *
   * A timeout is not fatal: proceeding and letting step 6 verify the binding is
   * strictly better than never joining at all.
   */
  async awaitRouteSettled(timeoutMs: number): Promise<BridgeResult> {
    const started = callBridge('awaitRouteSettled', (b) =>
      b.awaitRouteSettled(timeoutMs),
    );

    if (!started.ok) {
      // No host support: wait out the window blind, as before.
      await new Promise((resolve) => window.setTimeout(resolve, timeoutMs));
      return started;
    }

    return new Promise<BridgeResult>((resolve) => {
      const finish = (result: BridgeResult): void => {
        window.clearTimeout(timer);
        window.onRouteSettled = undefined;
        resolve(result);
      };

      const timer = window.setTimeout(
        () => finish(bridgeFail(`route did not settle within ${timeoutMs}ms`)),
        timeoutMs + ROUTE_SETTLE_GRACE_MS,
      );

      window.onRouteSettled = (settled: boolean) =>
        finish(
          settled ? bridgeOk(undefined) : bridgeFail('host reported no route change'),
        );
    });
  }

  /** §3.5 step 7 — output only, applied once, never re-applied. */
  async pinOutputToHdmi(): Promise<BridgeResult> {
    return callBridge('pinOutputToHdmi', (b) => b.pinOutputToHdmi());
  }

  async startStethoscope(): Promise<BridgeResult> {
    return callBridge('startStethoscope', (b) => b.startStethoscope());
  }

  async stopStethoscope(): Promise<BridgeResult> {
    if (!window.TelemedBridge) {
      // Nothing to stop is a success, not a failure — this runs on every
      // session start, including the first one in a fresh process.
      return bridgeOk(undefined);
    }
    return callBridge('stopStethoscope', (b) => b.stopStethoscope());
  }

  /**
   * The C driver's own counters (§9.3).
   *
   * These come straight from the capture engine now rather than being inferred,
   * which is what makes a *capture* failure distinguishable from a *transport*
   * one — the distinction that was previously only recoverable by correlating
   * two log streams by hand.
   */
  async stethoscopeStats(): Promise<BridgeResult<StethoscopeStats>> {
    const bridge = window.TelemedBridge;
    if (!bridge) {
      return bridgeFail('no native bridge present');
    }

    try {
      const stats = JSON.parse(bridge.stethoscopeStats()) as Partial<StethoscopeStats> & {
        active?: boolean;
        error?: string;
      };

      if (stats.error) {
        return bridgeFail(stats.error);
      }

      return bridgeOk({
        running: stats.active ?? false,
        sampleRate: bridge.getCaptureSampleRate(),
        packetsOk: stats.packetsOk ?? 0,
        packetsDropped: stats.packetsDropped ?? 0,
        overruns: stats.overruns ?? 0,
        deviceGone: stats.deviceGone ?? false,
      });
    } catch (error) {
      return bridgeFail(`stethoscope stats unreadable: ${String(error)}`);
    }
  }

  async notifyJoined(): Promise<BridgeResult> {
    return callBridge('notifyJoined', (b) => b.notifyJoined());
  }

  /** Sample rate the host negotiated, or 0 when it cannot say. */
  getCaptureSampleRate(): number {
    try {
      return window.TelemedBridge?.getCaptureSampleRate() ?? 0;
    } catch {
      return 0;
    }
  }

  /**
   * Receive the binary PCM channel (D3).
   *
   * The host posts a message whose data is the handshake string and whose
   * `ports` carry one end of a channel. On a host that predates this, the
   * message never arrives and the caller stays on the string path — which is
   * why the listener is installed unconditionally rather than gated on a
   * capability check that could disagree with reality.
   */
  onPcmPort(handler: (port: MessagePort) => void): void {
    this.pcmPortHandler = handler;

    if (this.pcmPortListener) {
      window.removeEventListener('message', this.pcmPortListener);
    }

    this.pcmPortListener = (event: MessageEvent): void => {
      if (event.data !== PCM_PORT_HANDSHAKE) return;

      const port = event.ports?.[0];
      if (!port) {
        // The handshake without a port means the host tried and failed; saying
        // so is better than silently continuing on the slow path.
        this.errorHandler?.('PCM channel handshake arrived without a port');
        return;
      }

      this.pcmPortHandler?.(port);
    };

    window.addEventListener('message', this.pcmPortListener);
    this.registeredListeners.push(this.pcmPortListener);
  }

  onDeviceState(handler: (state: UsbDeviceState) => void): void {
    this.deviceStateHandler = handler;
  }

  /**
   * Read from `getAudioState()`, which the host builds from UsbManager on
   * every call — so it reflects the device as it is now, however long ago it
   * was plugged in.
   */
  async currentStethoscopeState(): Promise<BridgeResult<UsbDeviceState>> {
    const bridge = window.TelemedBridge;
    if (!bridge) {
      return bridgeFail('no native bridge present');
    }

    try {
      const state = JSON.parse(bridge.getAudioState()) as Partial<NativeAudioState> & {
        ok?: boolean;
        error?: string;
      };
      if (state.ok === false || !state.stethoscope) {
        return bridgeFail(state.error ?? 'audio state carries no stethoscope section');
      }

      const { usbAttached, usbPermission } = state.stethoscope;
      return bridgeOk<UsbDeviceState>(
        !usbAttached ? 'ABSENT' : usbPermission ? 'READY' : 'PRESENT_NO_PERMISSION',
      );
    } catch (error) {
      return bridgeFail(`audio state unreadable: ${String(error)}`);
    }
  }

  onStethoscopeError(handler: (message: string) => void): void {
    this.errorHandler = handler;
  }

  onAudioRouteRestored(handler: (message: string) => void): void {
    this.routeRestoredHandler = handler;
  }

  dispose(): void {
    for (const name of this.registeredCallbacks) {
      // Leaving these attached would let a torn-down session keep receiving
      // host callbacks — residue that §10A.3 asserts against.
      delete (window as unknown as Record<string, unknown>)[name as string];
    }
    this.registeredCallbacks.length = 0;

    for (const listener of this.registeredListeners) {
      window.removeEventListener('message', listener);
    }
    this.registeredListeners.length = 0;
    this.pcmPortListener = null;

    this.pcmPortHandler = null;
    this.deviceStateHandler = null;
    this.errorHandler = null;
    this.routeRestoredHandler = null;
  }

  private registerHostCallbacks(): void {
    window.onStethoscopeAudioError = (message?: string) => {
      this.errorHandler?.(message ?? 'stethoscope audio error');
    };
    this.registeredCallbacks.push('onStethoscopeAudioError');

    window.onStethoscopePhysicallyDisconnected = () => {
      this.deviceStateHandler?.('LOST');
    };
    this.registeredCallbacks.push('onStethoscopePhysicallyDisconnected');

    window.onTaalConnected = () => {
      this.deviceStateHandler?.('READY');
    };
    this.registeredCallbacks.push('onTaalConnected');

    window.onTaalDisconnected = () => {
      this.deviceStateHandler?.('ABSENT');
    };
    this.registeredCallbacks.push('onTaalDisconnected');

    window.onStethoError = (reason: string) => {
      this.errorHandler?.(reason);
    };
    this.registeredCallbacks.push('onStethoError');

    window.onAudioRouteRestored = (message: string) => {
      this.routeRestoredHandler?.(message);
    };
    this.registeredCallbacks.push('onAudioRouteRestored');
  }

  /**
   * Whether a generation-stamped host call should be acted on.
   *
   * Exposed so the mic pause/resume handlers share one high-water mark.
   */
  acceptGeneration(generation: number): boolean {
    if (generation < this.highestGeneration) {
      return false;
    }
    this.highestGeneration = generation;
    return true;
  }
}
